/**
 * The printed map sheet.
 *
 * ONE canvas produces BOTH outputs. The PNG is this sheet; the PDF is this
 * sheet placed on a page and handed to the browser's print dialog. That is
 * deliberate: two renderers would drift, and "the PDF looks different from the
 * image I downloaded" is exactly the kind of difference nobody can explain
 * later.
 *
 * Laid out the way a map sheet has been laid out since long before screens: a
 * title band, the map in a framed neat-line, and a margin strip carrying the
 * things that let someone else trust the map — scale, north, date, source.
 */

import { getLineDashArray, getPointSymbol } from '../layers/symbols';
import type { LegendGroup, LegendSwatch, LegendSymbol } from './legendSymbols';

/** Page sizes in millimetres, portrait. */
const PAGE_MM: Record<string, [number, number]> = {
  A4: [210, 297],
  A3: [297, 420],
  Letter: [216, 279],
};

/** Print resolution. 150 is sharp on paper without producing a 40 MB PNG. */
const DPI = 150;
const MM_PER_INCH = 25.4;

export interface SheetOptions {
  pageSize: string;
  landscape: boolean;
  title: string;
  subtitle?: string;
  /** Drawn into the map frame. */
  map: HTMLCanvasElement;
  /** Ground metres per screen pixel of `map`, for the scale bar. */
  metresPerPixel: number;
  /** The application's own header logo, already loaded — drawn beside the
   *  title/subtitle exactly as it appears on the map. Absent/null draws no
   *  logo mark at all (the title simply starts at the margin), rather than
   *  inventing placeholder artwork for an app that never set one. */
  logoImage?: HTMLImageElement | null;
  /**
   * Layers to describe, already filtered to what the print extent actually
   * shows — both which layers (empty of features inside it are dropped)
   * and, for a unique-value renderer, which classes (see
   * utils/printLegendExtent.ts). Built by `collectLegend`, which knows
   * whether the app or the service chose each symbol; this module only
   * draws what it is handed.
   */
  legend?: LegendGroup[];
  /** Whether to reserve the legend column at all — distinct from `legend`
   *  being empty, which still reserves the column and says so (see
   *  renderLegend's own doc comment). */
  showLegend: boolean;
  showScaleBar: boolean;
  showNorthArrow: boolean;
  /** Also governs the "User:" line beside it — one admin-facing toggle for
   *  "who made this and when", not two. */
  showDate: boolean;
  /** The signed-in admin's own name, for the "User:" footer line. */
  userName?: string;
  attribution?: string;
}

export interface Sheet {
  canvas: HTMLCanvasElement;
  widthPx: number;
  heightPx: number;
  /** CSS page size for the print window, e.g. "A4 landscape". */
  cssPageSize: string;
}

const mmToPx = (mm: number) => Math.round((mm / MM_PER_INCH) * DPI);

/** A round number a scale bar can actually be labelled with. */
function niceDistance(metres: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(metres));
  for (const step of [1, 2, 5]) {
    if (metres <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

/* ── Legend symbols ────────────────────────────────────────────────────── */

/** Dash pattern in sheet pixels, scaled from the map's screen-pixel pattern. */
function dashPattern(pattern: string | undefined, scale: number): number[] {
  if (!pattern) return [];
  return pattern
    .split(/[,\s]+/)
    .map((part) => Number(part) * scale)
    .filter((value) => Number.isFinite(value) && value > 0);
}

/**
 * One legend symbol, drawn the way the map draws it.
 *
 * Point symbols reuse the SAME 24×24 path data the map marker is built from,
 * through `Path2D` — so the printed swatch is the artwork on screen rather
 * than an approximation of it. Lines and polygons are drawn to the shape a
 * legend has always used for them: a stroke for a line, a filled patch with
 * its outline for a polygon.
 */
function drawLegendSymbol(
  context: CanvasRenderingContext2D,
  symbol: LegendSymbol,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  context.save();
  context.lineCap = 'butt';
  context.lineJoin = 'round';

  if (symbol.geometry === 'point') {
    const definition = getPointSymbol(symbol.symbolId);
    // The marker's own size, capped so a large point symbol cannot push its
    // way out of the legend row.
    /* Most catalogue paths use about two thirds of their 24-unit box, so
       fitting the BOX to the row leaves the mark looking undersized next to a
       polygon patch that fills its row edge to edge. Scaling past the row and
       letting the artwork land at row height is what makes a legend read as
       one set of symbols rather than two. */
    const size = Math.min(height * 1.45, width, mmToPx(symbol.pointSize * 0.45));
    const unit = size / 24;

    context.translate(x + width / 2, y + height / 2);
    if (symbol.rotation) context.rotate((symbol.rotation * Math.PI) / 180);
    context.scale(unit, unit);
    context.translate(-12, -12);

    const path = new Path2D(definition.path);
    context.globalAlpha = symbol.fillOpacity;
    context.fillStyle = symbol.color;
    context.fill(path);

    if (symbol.outlineWidth > 0) {
      context.globalAlpha = 1;
      context.strokeStyle = symbol.outlineColor;
      /* The marker uses `vector-effect: non-scaling-stroke`, so its outline is
         a screen width rather than a shape width. Dividing by the scale
         reproduces that: the stroke lands at the intended thickness instead of
         growing with the symbol. */
      context.lineWidth = (symbol.outlineWidth * mmToPx(0.35)) / unit;
      context.stroke(path);
    }
    context.restore();
    return;
  }

  if (symbol.geometry === 'line') {
    /* Drawn as a shallow zig-zag, not a straight rule. A dash pattern is only
       legible over a length, and the bend is what tells a reader at a glance
       that the row describes a line rather than a border. */
    const midY = y + height / 2;
    const rise = height * 0.28;
    context.globalAlpha = symbol.fillOpacity;
    context.strokeStyle = symbol.color;
    context.lineWidth = Math.max(1, mmToPx(symbol.outlineWidth * 0.3));
    context.setLineDash(dashPattern(getLineDashArray(symbol.lineStyle), mmToPx(0.28)));
    context.beginPath();
    context.moveTo(x, midY + rise);
    context.lineTo(x + width * 0.36, midY - rise);
    context.lineTo(x + width * 0.64, midY + rise);
    context.lineTo(x + width, midY - rise);
    context.stroke();
    context.restore();
    return;
  }

  // Polygon, and anything whose geometry the service never reported — a filled
  // patch is the honest default, and reads as an area either way.
  const inset = Math.min(2, height * 0.12);
  const boxX = x;
  const boxY = y + inset;
  const boxW = width;
  const boxH = height - inset * 2;

  if (symbol.fillStyle !== 'transparent') {
    context.globalAlpha = symbol.fillOpacity;
    context.fillStyle = symbol.color;
    context.fillRect(boxX, boxY, boxW, boxH);
  }
  if (symbol.outlineWidth > 0) {
    context.globalAlpha = 1;
    context.strokeStyle = symbol.outlineColor;
    context.lineWidth = Math.max(0.6, mmToPx(symbol.outlineWidth * 0.28));
    context.setLineDash(dashPattern(getLineDashArray(symbol.outlineStyle), mmToPx(0.28)));
    context.strokeRect(boxX, boxY, boxW, boxH);
  }
  context.restore();
}

/** An empty frame, for a layer whose symbology could not be resolved. */
function drawPlaceholder(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  const boxW = Math.min(width, mmToPx(5));
  const boxH = Math.min(height, mmToPx(3));
  context.save();
  context.strokeStyle = '#cbd5e1';
  context.lineWidth = 0.8;
  context.setLineDash([mmToPx(0.5), mmToPx(0.5)]);
  context.strokeRect(x + (width - boxW) / 2, y + (height - boxH) / 2, boxW, boxH);
  context.restore();
}

/** A service swatch, fitted into the row without distorting it. */
function drawLegendImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
  pale = false,
) {
  if (pale) {
    /* A white symbol on a white legend is a blank row. A soft plate behind it
       gives the mark an edge to sit against, without recolouring what the
       service published — the symbol is still exactly its own colour. */
    const scaleForPlate = Math.min(width / image.width, height / image.height, 1);
    const plateW = image.width * scaleForPlate + mmToPx(1.8);
    const plateH = image.height * scaleForPlate + mmToPx(1.2);
    context.save();
    context.fillStyle = '#e2e8f0';
    context.strokeStyle = '#cbd5e1';
    context.lineWidth = 0.6;
    context.setLineDash([]);
    context.fillRect(x + (width - plateW) / 2, y + (height - plateH) / 2, plateW, plateH);
    context.strokeRect(x + (width - plateW) / 2, y + (height - plateH) / 2, plateW, plateH);
    context.restore();
  }

  const scale = Math.min(width / image.width, height / image.height, 1);
  const drawW = image.width * scale;
  const drawH = image.height * scale;
  context.drawImage(image, x + (width - drawW) / 2, y + (height - drawH) / 2, drawW, drawH);
}

/**
 * The legend column, laid out and drawn.
 *
 * ROWS ARE NOT ALL THE SAME THING
 * A layer with one symbol prints as a single row — swatch, then the layer's
 * name. A layer with classes prints its name as a heading and one indented row
 * per class, which is how a reader tells "these five colours belong together"
 * from "these are five layers".
 *
 * Fills the WHOLE frame it is given — a dedicated right-hand column, not an
 * inset box over the map — top-anchored under its own "LEGEND" heading.
 * Measured before it is drawn so rows that will not fit are dropped with a
 * count rather than silently clipped: a legend that quietly loses its last
 * three layers is worse than one that admits it. Draws the border and
 * heading even with nothing to list, since `groups` only ever holds classes
 * actually present in the print extent (see utils/printLegendExtent.ts) —
 * an extent with nothing of any checked layer inside it is a real, sayable
 * answer, not a bug to hide by leaving the column blank.
 */
function renderLegend(
  context: CanvasRenderingContext2D,
  groups: LegendGroup[],
  frame: { x: number; y: number; width: number; height: number },
) {
  const rowHeight = mmToPx(4.6);
  const headingHeight = mmToPx(4.2);
  const padding = mmToPx(3);
  const titleHeight = mmToPx(4.4);
  const swatchWidth = mmToPx(7);
  const gap = mmToPx(2);
  const indent = mmToPx(3);

  const nameSize = Math.round(mmToPx(2.9));
  const classSize = Math.round(mmToPx(2.6));

  /* Flattened to rows first, so the height is known before anything is drawn
     and the block can be anchored to the bottom of the frame. */
  type Row =
    | { kind: 'single'; text: string; swatch: LegendSwatch; note?: string }
    | { kind: 'note'; text: string }
    | { kind: 'heading'; text: string; note?: string }
    | { kind: 'class'; text: string; swatch: LegendSwatch }
    | { kind: 'more'; text: string };

  const rows: Row[] = [];
  for (const group of groups) {
    const swatches = group.swatches.filter(
      (swatch) => swatch.image || swatch.symbol || swatch.placeholder,
    );
    if (swatches.length === 0) continue;

    if (swatches.length === 1 && !swatches[0].label) {
      rows.push({ kind: 'single', text: group.name, swatch: swatches[0], note: group.note });
      /* The note sits on its own line rather than after the name: layer names
         are already long enough to be trimmed, and a note squeezed onto the
         same row would be the first thing lost. */
      if (group.note) rows.push({ kind: 'note', text: group.note });
    } else {
      rows.push({ kind: 'heading', text: group.name, note: group.note });
      for (const swatch of swatches) {
        rows.push({ kind: 'class', text: swatch.label || '—', swatch });
      }
      if (group.hiddenClasses) {
        rows.push({ kind: 'more', text: `+${group.hiddenClasses} more classes` });
      }
    }
  }
  const heightOf = (row: Row) =>
    row.kind === 'single' || row.kind === 'class' ? rowHeight : headingHeight;

  // As many rows as the column can take, leaving room for the "+N more" line.
  const available = frame.height - mmToPx(4) - titleHeight - padding * 2;
  let used = 0;
  let fits = 0;
  while (fits < rows.length && used + heightOf(rows[fits]) <= available) {
    used += heightOf(rows[fits]);
    fits += 1;
  }
  const hidden = rows.length - fits;
  const shown = rows.slice(0, fits);

  const boxX = frame.x;
  const boxY = frame.y;
  const boxWidth = frame.width;
  const boxHeight = frame.height;

  context.save();
  context.fillStyle = 'rgba(255, 255, 255, 0.93)';
  context.fillRect(boxX, boxY, boxWidth, boxHeight);
  context.strokeStyle = '#0f2942';
  context.lineWidth = 1;
  context.setLineDash([]);
  context.strokeRect(boxX, boxY, boxWidth, boxHeight);

  context.textAlign = 'left';
  context.textBaseline = 'middle';
  context.fillStyle = '#0f2942';
  context.font = `700 ${Math.round(mmToPx(2.6))}px Inter, system-ui, sans-serif`;
  context.fillText('LEGEND', boxX + padding, boxY + padding + titleHeight / 2);

  /** Trims a label to the width left for it, so nothing runs past the border. */
  const fit = (text: string, width: number) => {
    if (context.measureText(text).width <= width) return text;
    let clipped = text;
    while (clipped.length > 1 && context.measureText(`${clipped}…`).width > width) {
      clipped = clipped.slice(0, -1);
    }
    return `${clipped}…`;
  };

  if (rows.length === 0) {
    context.font = `400 ${classSize}px Inter, system-ui, sans-serif`;
    context.fillStyle = '#94a3b8';
    context.fillText(
      fit('Nothing in the printed area', boxWidth - padding * 2),
      boxX + padding,
      boxY + padding + titleHeight + rowHeight / 2,
    );
    context.restore();
    return;
  }

  let cursorY = boxY + padding + titleHeight;

  for (const row of shown) {
    const height = heightOf(row);
    const centre = cursorY + height / 2;

    if (row.kind === 'heading') {
      context.font = `600 ${nameSize}px Inter, system-ui, sans-serif`;
      context.fillStyle = '#0f2942';
      context.fillText(fit(row.text, boxWidth - padding * 2), boxX + padding, centre);
      cursorY += height;
      continue;
    }

    if (row.kind === 'note') {
      context.font = `400 ${classSize}px Inter, system-ui, sans-serif`;
      context.fillStyle = '#94a3b8';
      context.fillText(
        fit(row.text, boxWidth - padding * 2 - indent),
        boxX + padding + indent,
        centre,
      );
      cursorY += height;
      continue;
    }

    if (row.kind === 'more') {
      // Indented under its layer, so it reads as that layer's remainder rather
      // than as a note about the legend as a whole.
      context.font = `400 ${classSize}px Inter, system-ui, sans-serif`;
      context.fillStyle = '#94a3b8';
      context.fillText(
        fit(row.text, boxWidth - padding * 2 - indent),
        boxX + padding + indent,
        centre,
      );
      cursorY += height;
      continue;
    }

    const swatchX = boxX + padding + (row.kind === 'class' ? indent : 0);
    const swatchY = cursorY + mmToPx(0.5);
    const swatchH = height - mmToPx(1);

    if (row.swatch.image) {
      drawLegendImage(
        context, row.swatch.image, swatchX, swatchY, swatchWidth, swatchH, row.swatch.pale,
      );
    } else if (row.swatch.symbol) {
      drawLegendSymbol(context, row.swatch.symbol, swatchX, swatchY, swatchWidth, swatchH);
    } else if (row.swatch.placeholder) {
      drawPlaceholder(context, swatchX, swatchY, swatchWidth, swatchH);
    }

    const textX = swatchX + swatchWidth + gap;
    const textWidth = boxX + boxWidth - padding - textX;

    context.font =
      row.kind === 'class'
        ? `400 ${classSize}px Inter, system-ui, sans-serif`
        : `500 ${nameSize}px Inter, system-ui, sans-serif`;
    context.fillStyle = row.kind === 'class' ? '#475569' : '#0f2942';
    context.setLineDash([]);
    context.globalAlpha = 1;
    context.fillText(fit(row.text, textWidth), textX, centre);

    cursorY += height;
  }

  if (hidden > 0) {
    context.font = `400 ${classSize}px Inter, system-ui, sans-serif`;
    context.fillStyle = '#94a3b8';
    context.fillText(
      `+${hidden} more not shown`,
      boxX + padding,
      cursorY + rowHeight / 2,
    );
  }

  context.restore();
}

function drawNorthArrow(context: CanvasRenderingContext2D, x: number, y: number, size: number) {
  context.save();
  context.translate(x, y);
  context.fillStyle = '#0f2942';
  context.strokeStyle = '#0f2942';
  context.lineWidth = Math.max(1, size * 0.04);

  // A split needle: solid east half, hollow west half — the convention that
  // reads as a compass rather than as a triangle.
  context.beginPath();
  context.moveTo(0, -size * 0.5);
  context.lineTo(size * 0.22, size * 0.42);
  context.lineTo(0, size * 0.22);
  context.closePath();
  context.fill();

  context.beginPath();
  context.moveTo(0, -size * 0.5);
  context.lineTo(-size * 0.22, size * 0.42);
  context.lineTo(0, size * 0.22);
  context.closePath();
  context.stroke();

  context.font = `600 ${Math.round(size * 0.3)}px Inter, system-ui, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'top';
  context.fillText('N', 0, size * 0.5);
  context.restore();
}

/** The app's own header logo, clipped into a circle — matches how the live
 *  header itself shows it (a round mark beside the title). */
function drawCircularLogo(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  cx: number,
  cy: number,
  diameter: number,
) {
  const radius = diameter / 2;
  context.save();
  context.beginPath();
  context.arc(cx, cy, radius, 0, Math.PI * 2);
  context.closePath();
  context.strokeStyle = '#0f2942';
  context.lineWidth = Math.max(1, diameter * 0.04);
  context.clip();

  // Covers the circle without distorting: the logo keeps its own aspect
  // ratio and is centred, cropped to a square first if it isn't one.
  const sourceSize = Math.min(image.naturalWidth || image.width, image.naturalHeight || image.height);
  const sourceX = ((image.naturalWidth || image.width) - sourceSize) / 2;
  const sourceY = ((image.naturalHeight || image.height) - sourceSize) / 2;
  context.drawImage(
    image,
    sourceX, sourceY, sourceSize, sourceSize,
    cx - radius, cy - radius, diameter, diameter,
  );
  context.restore();

  context.save();
  context.beginPath();
  context.arc(cx, cy, radius, 0, Math.PI * 2);
  context.strokeStyle = '#0f2942';
  context.lineWidth = Math.max(1, diameter * 0.04);
  context.stroke();
  context.restore();
}

function drawScaleBar(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  maxWidth: number,
  metresPerPixel: number,
) {
  const targetMetres = niceDistance(metresPerPixel * maxWidth);
  const barWidth = targetMetres / metresPerPixel;
  const height = Math.max(6, maxWidth * 0.03);

  // Alternating blocks, so the bar can be read off in halves at a glance.
  const blocks = 4;
  for (let index = 0; index < blocks; index += 1) {
    context.fillStyle = index % 2 === 0 ? '#0f2942' : '#ffffff';
    context.fillRect(x + (barWidth / blocks) * index, y, barWidth / blocks, height);
  }
  context.strokeStyle = '#0f2942';
  context.lineWidth = 1;
  context.strokeRect(x, y, barWidth, height);

  const label =
    targetMetres >= 1000 ? `${(targetMetres / 1000).toLocaleString()} km` : `${targetMetres} m`;
  context.fillStyle = '#0f2942';
  context.font = `600 ${Math.round(height * 1.5)}px Inter, system-ui, sans-serif`;
  context.textAlign = 'left';
  context.textBaseline = 'top';
  context.fillText('0', x, y + height + 4);
  context.textAlign = 'right';
  context.fillText(label, x + barWidth, y + height + 4);
}

/**
 * The sheet's layout, in millimetres, before anything is drawn.
 *
 * The ONE place the page is divided into header / map / legend-column /
 * footer — `printFrameAspectRatio` and `renderPrintSheet` both build on this
 * instead of each stating the same numbers, which is what let them drift out
 * of step the first time this was two separate calculations.
 */
function computeLayoutMm(pageSize: string, landscape: boolean, hasSubtitle: boolean, showLegend: boolean) {
  const [portraitW, portraitH] = PAGE_MM[pageSize] ?? PAGE_MM.A4;
  const widthMm = landscape ? portraitH : portraitW;
  const heightMm = landscape ? portraitW : portraitH;
  const margin = 12;
  const logoSize = 10;
  const titleBlockHeight = 6 + 1.5 + (hasSubtitle ? 3.4 + 1.5 : 0);
  // The logo (when there is one) sits beside the title/subtitle block, so the
  // band is whichever of the two is taller. The rule is drawn at the bottom
  // of that band; `headerGap` is the breathing room BELOW the rule, before
  // the map/legend content actually starts.
  const headerBandHeight = Math.max(logoSize, titleBlockHeight);
  const headerGap = 4;
  const headerHeight = headerBandHeight + headerGap;
  const footerHeight = 26;
  const legendColWidth = showLegend ? 56 : 0;
  const legendGap = showLegend ? 4 : 0;

  const contentTop = margin + headerHeight;
  const contentBottom = heightMm - margin - footerHeight;
  const mapFrameW = widthMm - margin * 2 - legendColWidth - legendGap;
  const mapFrameH = contentBottom - contentTop;

  return {
    widthMm, heightMm, margin, logoSize, headerBandHeight, headerHeight, footerHeight,
    legendColWidth, legendGap, contentTop, contentBottom, mapFrameW, mapFrameH,
  };
}

/**
 * The width:height ratio of the actual MAP FRAME inside a printed sheet —
 * not the page's own ratio, which is bigger (the header band, the footer
 * strip, and the legend column all eat into it). Exported so the Print tool
 * can show an on-map box at exactly this ratio before anything is captured:
 * the capture used to always grab the whole on-screen viewport, whatever
 * shape the browser window happened to be, and `renderPrintSheet`'s own
 * crop-to-fit math then silently cut off whatever didn't match the page's
 * shape — so what printed was never quite what the screen showed. Showing
 * this box up front and cropping exactly to it at capture time (see
 * `buildSheet` in App.tsx) makes the two agree.
 *
 * Mirrors `renderPrintSheet`'s own layout arithmetic (via `computeLayoutMm`)
 * in millimetres rather than pixels — a ratio is the same either way, and
 * millimetres skip the DPI constant entirely.
 */
export function printFrameAspectRatio(
  pageSize: string,
  landscape: boolean,
  hasSubtitle: boolean,
  showLegend: boolean,
): number {
  const layout = computeLayoutMm(pageSize, landscape, hasSubtitle, showLegend);
  return layout.mapFrameW / Math.max(layout.mapFrameH, 1);
}

export function renderPrintSheet(options: SheetOptions): Sheet {
  const hasSubtitle = Boolean(options.subtitle?.trim());
  const layout = computeLayoutMm(options.pageSize, options.landscape, hasSubtitle, options.showLegend);

  const width = mmToPx(layout.widthMm);
  const height = mmToPx(layout.heightMm);
  const margin = mmToPx(layout.margin);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not prepare the page.');

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);

  /* ── Outer border — the sheet's own frame, close to the page edge. ── */
  const borderInset = mmToPx(3);
  context.strokeStyle = '#0f2942';
  context.lineWidth = mmToPx(1.1);
  context.strokeRect(borderInset / 2, borderInset / 2, width - borderInset, height - borderInset);

  /* ── Header band: logo + title/subtitle on the left, north arrow on the
     right — both vertically centred on the same row, the way the live
     on-screen header lays itself out. ── */
  const titleSize = Math.round(mmToPx(6));
  const subtitleSize = Math.round(mmToPx(3.4));
  const logoSize = mmToPx(layout.logoSize);
  const headerCentreY = margin + mmToPx(layout.headerBandHeight) / 2;

  let titleX = margin;
  if (options.logoImage) {
    drawCircularLogo(context, options.logoImage, margin + logoSize / 2, headerCentreY, logoSize);
    titleX = margin + logoSize + mmToPx(3);
  }

  const titleBlockHeight = titleSize + mmToPx(1.5) + (hasSubtitle ? subtitleSize + mmToPx(1.5) : 0);
  let cursorY = headerCentreY - titleBlockHeight / 2;

  context.fillStyle = '#0f2942';
  context.textAlign = 'left';
  context.textBaseline = 'top';
  context.font = `700 ${titleSize}px Inter, system-ui, sans-serif`;
  context.fillText(options.title || 'Map', titleX, cursorY);
  cursorY += titleSize + mmToPx(1.5);

  if (hasSubtitle) {
    context.fillStyle = '#64748b';
    context.font = `400 ${subtitleSize}px Inter, system-ui, sans-serif`;
    context.fillText(options.subtitle!.trim(), titleX, cursorY);
  }

  if (options.showNorthArrow) {
    drawNorthArrow(context, width - margin - mmToPx(6), headerCentreY, mmToPx(10));
  }

  // The rule at the bottom of the header band — the width of the sheet's
  // live area, same as before.
  const ruleY = margin + mmToPx(layout.headerBandHeight);
  context.strokeStyle = '#0f2942';
  context.lineWidth = mmToPx(0.5);
  context.beginPath();
  context.moveTo(margin, ruleY);
  context.lineTo(width - margin, ruleY);
  context.stroke();

  /* ── Map frame (left) + legend column (right) ── */
  const frameX = margin;
  const frameY = mmToPx(layout.contentTop);
  const frameW = mmToPx(layout.mapFrameW);
  const frameH = mmToPx(layout.mapFrameH);

  context.save();
  context.beginPath();
  context.rect(frameX, frameY, frameW, frameH);
  context.clip();

  /* Cover the frame without distorting: the map keeps its aspect ratio and is
     centred, which is what a map sheet does — a stretched map is a wrong map. */
  const sourceRatio = options.map.width / options.map.height;
  const frameRatio = frameW / frameH;
  let drawW = frameW;
  let drawH = frameH;
  if (sourceRatio > frameRatio) drawW = frameH * sourceRatio;
  else drawH = frameW / sourceRatio;

  context.drawImage(
    options.map,
    frameX + (frameW - drawW) / 2,
    frameY + (frameH - drawH) / 2,
    drawW,
    drawH,
  );
  context.restore();

  // Neat-line.
  context.strokeStyle = '#0f2942';
  context.lineWidth = mmToPx(0.4);
  context.strokeRect(frameX, frameY, frameW, frameH);

  if (options.showLegend) {
    renderLegend(context, options.legend ?? [], {
      x: frameX + frameW + mmToPx(layout.legendGap),
      y: frameY,
      width: mmToPx(layout.legendColWidth),
      height: frameH,
    });
  }

  /* ── Footer band: scale bar + "User / Date" on the left, the disclaimer
     on the right. ── */
  const footerTop = height - margin - mmToPx(layout.footerHeight);
  let footerCursorY = footerTop + mmToPx(3);

  if (options.showScaleBar) {
    drawScaleBar(context, margin, footerCursorY, mmToPx(40), options.metresPerPixel);
    footerCursorY += mmToPx(11);
  }

  if (options.showDate) {
    context.fillStyle = '#334155';
    context.font = `500 ${Math.round(mmToPx(2.9))}px Inter, system-ui, sans-serif`;
    context.textAlign = 'left';
    context.textBaseline = 'top';
    context.fillText(
      `User: ${options.userName?.trim() || '—'}    Date: ${new Date().toLocaleDateString()}`,
      margin,
      footerCursorY,
    );
  }

  // The disclaimer is fixed boilerplate, not an admin-editable field — a
  // printed map is exactly the kind of document that needs the same legal
  // line every time, not a reminder someone can forget to type in.
  const disclaimerLines = [
    'Disclaimer: For reference only.',
    'Not for legal or survey use.',
    '© MapGeoid Pvt. Ltd.',
  ];
  if (options.attribution?.trim()) disclaimerLines.push(options.attribution.trim());

  context.fillStyle = '#64748b';
  context.font = `400 ${Math.round(mmToPx(2.5))}px Inter, system-ui, sans-serif`;
  context.textAlign = 'right';
  context.textBaseline = 'bottom';
  disclaimerLines
    .slice()
    .reverse()
    .forEach((line, indexFromBottom) => {
      context.fillText(line, width - margin, height - margin - mmToPx(3.2) * indexFromBottom);
    });

  return {
    canvas,
    widthPx: width,
    heightPx: height,
    cssPageSize: `${options.pageSize} ${options.landscape ? 'landscape' : 'portrait'}`,
  };
}

/** Opens the browser's print dialog on the sheet. "Save as PDF" lives there. */
export function printSheet(sheet: Sheet, title: string): void {
  const win = window.open('', '_blank', 'width=1100,height=800');
  if (!win) throw new Error('The print window was blocked. Allow pop-ups for this site.');

  const dataUrl = sheet.canvas.toDataURL('image/png');
  win.document.write(`<!doctype html>
<html>
<head>
<title>${title.replace(/[<>&]/g, '')}</title>
<style>
  @page { size: ${sheet.cssPageSize}; margin: 0; }
  html, body { margin: 0; padding: 0; background: #525659; }
  img { display: block; width: 100%; height: auto; }
  @media print { body { background: #fff; } }
</style>
</head>
<body><img src="${dataUrl}" alt="${title.replace(/[<>&"]/g, '')}"></body>
</html>`);
  win.document.close();

  /* Waits for the image to decode before printing. Printing a page whose only
     content has not loaded yields a blank sheet, and the dialog gives no hint
     why. */
  const image = win.document.querySelector('img');
  const go = () => {
    win.focus();
    win.print();
  };
  if (image && !image.complete) image.addEventListener('load', go, { once: true });
  else win.setTimeout(go, 150);
}

/** Saves the sheet as a PNG. */
export function downloadSheet(sheet: Sheet, filename: string): void {
  sheet.canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.png') ? filename : `${filename}.png`;
    link.click();
    URL.revokeObjectURL(url);
  }, 'image/png');
}
